import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import Chip from '@mui/material/Chip';

interface AutocompleteSelectProps {
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  options: string[];
  selectedOptions: string[];
  setSelectedOptions: (newOptions: string[]) => void;
  width?: string;
  checkboxMargin?: string;
  allowCustomValue?: boolean; 
}

export default function AutocompleteSelect({
  searchTerm,
  onSearchTermChange,
  options,
  selectedOptions,
  setSelectedOptions,
  width = '380px',
  checkboxMargin = '8px',
  allowCustomValue = false,
}: AutocompleteSelectProps) {

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string[]) => {
    if (allowCustomValue && searchTerm.trim() && !newValue.includes(searchTerm.trim())) {
      setSelectedOptions([...newValue, searchTerm.trim()]); 
    } else {
      setSelectedOptions(newValue);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
    onSearchTermChange(value);
  };

  return (
    <Autocomplete
      sx={{ width }}
      multiple
      options={options}
      disableCloseOnSelect
      freeSolo={allowCustomValue} 
      renderTags={(value: string[], getTagProps) => {
        const maxVisibleChips = 2;
        const visibleChips = value
          .slice(0, maxVisibleChips)
          .map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} label={option} {...tagProps} />;
          });
        const remainingChips = value.length - visibleChips.length;
        if (remainingChips > 0) {
          visibleChips.push(<Chip key="more" label={`+${remainingChips}...`} />);
        }
        return visibleChips;
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={<CheckBoxOutlineBlank />}
            checkedIcon={<CheckBox />}
            style={{ marginRight: checkboxMargin }}
            checked={selected}
          />
          {option}
        </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Search skills"
          placeholder="Select skills"
        />
      )}
      onChange={handleChange}
      value={selectedOptions}
      inputValue={searchTerm}
      onInputChange={handleInputChange}
    />
  );
}
